// #tag: COMMON CARD AND PRESENTATION COMPONENTS
// #purpose: Standard UI card primitives for exam results, student vault documents, KPI metrics, category filter chips, and milestone timelines.
import React from 'react';
import { ArrowRight, ExternalLink, Heart, MoreVertical, Eye, Download, Edit2, RefreshCw, ListOrdered, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ExamResult } from '../../types';
import { useSavedExams } from '../../context/AuthProvider';
import { Illustration } from './Illustration';
import { vaultDocumentIllustration } from '../../lib/vaultIllustration';
import { safeExternalUrl } from '../../lib/safeExternalUrl';

// #tag: EXAM RESULT CARD COMPONENT
// #purpose: Renders individual exam scorecards, category tags, merit flags, bookmark triggers, and deep links.
export const ResultCard = React.memo(function ResultCard({ result }: { result: ExamResult }) {
  const navigate = useNavigate();
  const { isSaved, toggleSave } = useSavedExams();

  const saved = isSaved(result.id);
  const officialLink = safeExternalUrl(result.resultUrl);

  return (
    <article className="wfx-result-card glass-surface">
      <div className="wfx-result-card-head">
        <div className="wfx-result-badges">
          {result.isNew && <span className="chip-pill is-new">New</span>}
          <span className="chip-pill wfx-category-badge">{result.examCategory}</span>
        </div>
        <button
          type="button"
          className={`wfx-save-btn ${saved ? 'is-saved' : ''}`}
          aria-label={saved ? `Remove ${result.title} from saved exams` : `Save ${result.title}`}
          title={saved ? 'Remove from saved' : 'Save exam'}
          onClick={() =>
            toggleSave({
              id: result.id,
              title: result.title,
              organization: result.organization,
              examCategory: result.examCategory,
              savedAt: new Date().toISOString(),
            })
          }
        >
          <Heart size={18} fill={saved ? 'currentColor' : 'none'} aria-hidden="true" />
        </button>
      </div>
      <div className="wfx-result-card-body">
        <h3 title={result.title}>{result.title}</h3>
        <p className="wfx-muted wfx-result-org" title={result.organization}>{result.organization}</p>
        <div className="wfx-result-tags" role="list" aria-label="Result details available">
          {result.hasScoreCard && <span className="wfx-tag-badge" role="listitem">Score Card</span>}
          {result.hasMeritList && <span className="wfx-tag-badge" role="listitem">Merit List</span>}
          {result.hasCutoff && <span className="wfx-tag-badge" role="listitem">Cutoff</span>}
          {result.hasCounselling && <span className="wfx-tag-badge" role="listitem">Counselling</span>}
        </div>
      </div>
      <div className="wfx-result-card-actions">
        <button
          type="button"
          className="wfx-btn wfx-btn-primary"
          onClick={() => navigate(`/results/${result.id}`)}
          aria-label={`View details for ${result.title}`}
        >
          <span>View details</span>
          <ArrowRight size={15} aria-hidden="true" />
        </button>
        {officialLink && (
          <a
            href={officialLink}
            target="_blank"
            rel="noopener noreferrer"
            className="wfx-btn wfx-btn-secondary"
            aria-label={`Official portal for ${result.title}`}
            title="Official portal"
          >
            <span>Official link</span>
            <ExternalLink size={14} aria-hidden="true" />
          </a>
        )}
      </div>
    </article>
  );
});

export interface DocumentCardProps {
  document?: {
    id: string;
    name: string;
    categoryId?: string;
    subcategory: string;
    uploadedAt: string;
    expiryStatus?: 'valid' | 'expiring' | 'expired';
    expiryDate?: string;
    fileSize: string;
    version: number;
    fileName?: string;
    sequenceOrder?: number | null;
  };
  name?: string;
  categoryId?: string;
  subcategory?: string;
  uploadedAt?: string;
  fileSize?: string;
  version?: number;
  expiryStatus?: 'valid' | 'expiring' | 'expired';
  expiryDate?: string;
  sequenceOrder?: number | null;
  highlighted?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectToggle?: () => void;
  onPreview?: () => void;
  onDownload?: () => void;
  onSetSequence?: () => void;
  onRename?: () => void;
  onReplace?: () => void;
  onDelete?: () => void;
}

// #tag: DELETE DOCUMENT CONFIRMATION
// #purpose: Prevents accidental deletion of a saved document.
// #tag: DOCUMENT ACTION MENU
// #purpose: Provides secondary document management actions without increasing card height.
const DocumentActionMenu = React.memo(function DocumentActionMenu({
  name,
  onSetSequence,
  onRename,
  onReplace,
  onDelete,
  onToggle,
}: {
  name: string;
  onSetSequence?: () => void;
  onRename?: () => void;
  onReplace?: () => void;
  onDelete?: () => void;
  onToggle?: (open: boolean) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const handleOpen = React.useCallback((next: boolean) => {
    setOpen(next);
    onToggle?.(next);
  }, [onToggle]);

  React.useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        handleOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleOpen]);

  return (
    <div className="wfx-doc-menu-wrap" ref={menuRef}>
      <button
        type="button"
        className="wfx-doc-menu-trigger"
        aria-label={`More actions for ${name}`}
        aria-haspopup="true"
        aria-expanded={open}
        title="More actions"
        onClick={(e) => {
          e.stopPropagation();
          handleOpen(!open);
        }}
      >
        <MoreVertical size={18} aria-hidden="true" />
      </button>

      {open && (
        <div className="wfx-doc-dropdown-menu glass-surface" role="menu" onClick={(e) => e.stopPropagation()}>
          {onSetSequence && (
            <button
              type="button"
              className="wfx-doc-menu-item"
              role="menuitem"
              onClick={() => {
                handleOpen(false);
                onSetSequence();
              }}
            >
              <ListOrdered size={15} aria-hidden="true" />
              <span>Set position</span>
            </button>
          )}
          {onRename && (
            <button
              type="button"
              className="wfx-doc-menu-item"
              role="menuitem"
              onClick={() => {
                handleOpen(false);
                onRename();
              }}
            >
              <Edit2 size={15} aria-hidden="true" />
              <span>Rename</span>
            </button>
          )}
          {onReplace && (
            <button
              type="button"
              className="wfx-doc-menu-item"
              role="menuitem"
              onClick={() => {
                handleOpen(false);
                onReplace();
              }}
            >
              <RefreshCw size={15} aria-hidden="true" />
              <span>Replace</span>
            </button>
          )}
          {onDelete && (
            <>
              <div className="wfx-doc-menu-divider" role="separator" />
              <button
                type="button"
                className="wfx-doc-menu-item wfx-doc-menu-item--danger"
                role="menuitem"
                onClick={() => {
                  handleOpen(false);
                  onDelete();
                }}
              >
                <Trash2 size={15} aria-hidden="true" />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
});

// #tag: MOBILE DOCUMENT CARD
// #purpose: Displays a saved vault document with compact primary actions.
export const DocumentCard = React.memo(function DocumentCard(props: DocumentCardProps) {
  const doc = props.document;
  const name = doc?.name ?? props.name ?? 'Document';
  const categoryId = doc?.categoryId ?? props.categoryId ?? 'other';
  const subcategory = doc?.subcategory ?? props.subcategory ?? 'Miscellaneous';
  const uploadedAt = doc?.uploadedAt ?? props.uploadedAt ?? '';
  const fileSize = doc?.fileSize ?? props.fileSize ?? '';
  const version = doc?.version ?? props.version ?? 1;
  const expiryStatus = doc?.expiryStatus ?? props.expiryStatus ?? 'valid';
  const expiryDate = doc?.expiryDate ?? props.expiryDate;
  const sequenceOrder = doc?.sequenceOrder ?? props.sequenceOrder;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { highlighted, selectable, selected, onSelectToggle, onPreview, onDownload, onSetSequence, onRename, onReplace, onDelete } = props;

  const statusLabel =
    expiryStatus === 'expired'
      ? 'Expired'
      : expiryStatus === 'expiring'
        ? `Expiring (${expiryDate ?? 'soon'})`
        : null;

  const illustrationContext = vaultDocumentIllustration(categoryId, subcategory);

  const cardClass = [
    'wfx-doc-card',
    'glass-surface',
    highlighted ? 'is-highlighted' : '',
    selectable ? 'is-selectable' : '',
    selected ? 'is-selected' : '',
    menuOpen ? 'is-menu-open' : '',
  ].filter(Boolean).join(' ');

  const handleCardClick = () => {
    if (selectable) onSelectToggle?.();
  };

  const handleCardKeyDown = (event: React.KeyboardEvent) => {
    if (!selectable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectToggle?.();
    }
  };

  return (
    <article
      className={cardClass}
      onClick={selectable ? handleCardClick : undefined}
      onKeyDown={selectable ? handleCardKeyDown : undefined}
      tabIndex={selectable ? 0 : undefined}
      role={selectable ? 'button' : undefined}
      aria-pressed={selectable ? selected : undefined}
    >
      {/* Top Header Row: sequence order / selection on left, ⋮ on right */}
      <div className="wfx-doc-card-top">
        <div className="wfx-doc-card-badges">
          {selectable && (
            <label
              className="wfx-doc-select"
              onClick={(event) => event.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={onSelectToggle}
                aria-label={`Select ${name}`}
              />
            </label>
          )}
          {typeof sequenceOrder === 'number' && sequenceOrder > 0 && (
            <span className="wfx-doc-sequence-badge" title={`Position ${sequenceOrder} in vault sequence`}>
              #{sequenceOrder}
            </span>
          )}
          <span className="chip-pill wfx-doc-type-pill">{subcategory}</span>
          {statusLabel && <span className={`wfx-expiry wfx-expiry--${expiryStatus}`}>{statusLabel}</span>}
        </div>

        {!selectable && (onSetSequence || onRename || onReplace || onDelete) && (
          <DocumentActionMenu
            name={name}
            onSetSequence={onSetSequence}
            onRename={onRename}
            onReplace={onReplace}
            onDelete={onDelete}
            onToggle={setMenuOpen}
          />
        )}
      </div>

      {/* Main horizontal content: Thumbnail (left) + Details & Primary Actions (right) */}
      <div className="wfx-doc-card-content">
        <div className="wfx-doc-thumbnail-wrap">
          <Illustration
            context={illustrationContext}
            size="xs"
            className="wfx-doc-card-illustration"
            loading="lazy"
          />
        </div>

        <div className="wfx-doc-info">
          <h4 title={name} className="wfx-doc-title">{name}</h4>
          <div className="wfx-doc-meta">
            {uploadedAt && <span>{uploadedAt}</span>}
            {uploadedAt && fileSize && <span className="wfx-meta-sep">·</span>}
            {fileSize && <span>{fileSize}</span>}
            {fileSize && <span className="wfx-meta-sep">·</span>}
            <span>v{version}</span>
          </div>

          {!selectable && (onPreview || onDownload) && (
            <div className="wfx-doc-primary-actions">
              {onPreview && (
                <button
                  type="button"
                  className="wfx-doc-btn wfx-doc-btn--preview"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview();
                  }}
                >
                  <Eye size={14} aria-hidden="true" />
                  <span>Preview</span>
                </button>
              )}
              {onDownload && (
                <button
                  type="button"
                  className="wfx-doc-btn wfx-doc-btn--download"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload();
                  }}
                >
                  <Download size={14} aria-hidden="true" />
                  <span>Download</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
});

// #tag: KPI STATISTIC METRIC CARD
// #purpose: Displays an individual key performance indicator with dynamic tone accents and optional context illustration.
export function StatCard({
  label,
  value,
  tone = 'orange',
  illustration,
}: {
  label: string;
  value: string | number;
  tone?: string;
  illustration?: string;
}) {
  return (
    <div className={`stat-card wfx-stat-card tone-${tone}${illustration ? ' has-illustration' : ''}`}>
      {illustration && (
        <Illustration context={illustration} size="xs" className="wfx-stat-card-illustration" />
      )}
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

// #tag: FILTER CHIP SELECTOR GROUP
// #purpose: Renders an accessible, horizontally scrollable group of selectable category pill buttons.
export function FilterChips<T extends string>({
  options,
  selected,
  onChange,
  allLabel = 'All',
}: {
  options: readonly T[];
  selected: T | 'all';
  onChange: (value: T | 'all') => void;
  allLabel?: string;
}) {
  return (
    <div className="wfx-filter-chips" role="group" aria-label="Filters">
      <button
        type="button"
        className={`chip-pill ${selected === 'all' ? 'is-active' : ''}`}
        onClick={() => onChange('all')}
      >
        {allLabel}
      </button>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`chip-pill ${selected === opt ? 'is-active' : ''}`}
          onClick={() => onChange(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// #tag: SECTION HEADING BLOCK
// #purpose: Standard page section header with kicker tag, title, lead copy, and trailing action elements.
export function SectionHeader({
  kicker,
  title,
  description,
  action,
}: {
  kicker?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="wfx-section-header">
      <div>
        {kicker && <span className="section-kicker">{kicker}</span>}
        <h1>{title}</h1>
        {description && <p className="wfx-lead">{description}</p>}
      </div>
      {action}
    </header>
  );
}

// #tag: EXAMINATION TIMELINE COMPONENT
// #purpose: Visualizes recruitment stages and milestones with completed, active, and upcoming indicators.
export function TimelineView({
  steps,
  layout = 'horizontal',
}: {
  steps: { id: string; label: string; status: 'completed' | 'upcoming' | 'pending'; date?: string }[];
  layout?: 'horizontal' | 'vertical';
}) {
  return (
    <ol className={`wfx-timeline${layout === 'horizontal' ? ' is-horizontal' : ''}`}>
      {steps.map((step) => (
        <li key={step.id} className={`wfx-timeline-step is-${step.status}`}>
          <span className="wfx-timeline-marker" aria-hidden="true">
            {step.status === 'completed' ? '✔' : step.status === 'upcoming' ? '→' : '○'}
          </span>
          <div className="wfx-timeline-copy">
            <strong>{step.label}</strong>
            {step.date && <span className="wfx-muted">{step.date}</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}
